/**
 * DexScreener API Service
 * Free API with 300-600 requests per minute rate limits
 * Used for market cap, volume, and pair data
 * 
 * Reference: https://docs.dexscreener.com/api/reference
 */

const DEXSCREENER_BASE_URL = 'https://api.dexscreener.com';

/**
 * Get token pairs by token address (300 requests/min)
 * Returns market cap, volume, liquidity, price data
 * 
 * @param {string} chainId - Chain ID (e.g., "solana", "ethereum")
 * @param {string} tokenAddress - Token contract address
 * @returns {Promise<Array>} Array of pair objects
 */
export const getTokenPairs = async (chainId, tokenAddress) => {
  try {
    const url = `${DEXSCREENER_BASE_URL}/token-pairs/v1/${chainId}/${tokenAddress}`;
    
    console.log(`🔍 [DexScreener API] Fetching: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    console.log(`📡 [DexScreener API] Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [DexScreener API] Error response:`, {
        status: response.status,
        statusText: response.statusText,
        url: url,
        errorText: errorText
      });
      throw new Error(`DexScreener API error: ${response.status} - ${errorText.substring(0, 200)}`);
    }

    const data = await response.json();
    console.log(`✅ [DexScreener API] Response data:`, {
      type: typeof data,
      isArray: Array.isArray(data),
      length: Array.isArray(data) ? data.length : 'N/A',
      firstItem: Array.isArray(data) && data.length > 0 ? data[0] : null,
      firstItemKeys: Array.isArray(data) && data.length > 0 ? Object.keys(data[0]) : null,
      firstItemPairAddress: Array.isArray(data) && data.length > 0 ? data[0]?.pairAddress : null
    });
    
    // DexScreener returns an array directly
    const pairsArray = Array.isArray(data) ? data : [];
    
    // Log each pair's pairAddress for debugging
    pairsArray.forEach((pair, index) => {
      console.log(`📊 [DexScreener] Pair ${index}:`, {
        pairAddress: pair?.pairAddress,
        hasPairAddress: !!pair?.pairAddress,
        pairKeys: Object.keys(pair || {})
      });
    });
    
    return pairsArray;
  } catch (error) {
    console.error('❌ [DexScreener API] Exception caught:', {
      error: error,
      message: error?.message,
      url: `${DEXSCREENER_BASE_URL}/token-pairs/v1/${chainId}/${tokenAddress}`
    });
    throw error;
  }
};

/**
 * Get pairs by chain and pair address (300 requests/min)
 * 
 * @param {string} chainId - Chain ID (e.g., "solana")
 * @param {string} pairAddress - Pair address
 * @returns {Promise<Object>} Pair data object
 */
export const getPairByAddress = async (chainId, pairAddress) => {
  try {
    const url = `${DEXSCREENER_BASE_URL}/latest/dex/pairs/${chainId}/${pairAddress}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`DexScreener API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching pair from DexScreener:', error);
    throw error;
  }
};

/**
 * Get multiple tokens by addresses (300 requests/min)
 * Up to 30 addresses comma-separated
 * 
 * @param {string} chainId - Chain ID
 * @param {string|Array<string>} tokenAddresses - Single address or array of addresses (max 30)
 * @returns {Promise<Array>} Array of token data
 */
export const getTokensByAddresses = async (chainId, tokenAddresses) => {
  try {
    const addresses = Array.isArray(tokenAddresses) 
      ? tokenAddresses.join(',') 
      : tokenAddresses;
    
    const url = `${DEXSCREENER_BASE_URL}/tokens/v1/${chainId}/${addresses}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`DexScreener API error: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching tokens from DexScreener:', error);
    throw error;
  }
};

/**
 * Extract market cap and volume from DexScreener pair data
 * 
 * @param {Array} pairs - Array of pair objects from DexScreener
 * @returns {Object} { marketCap, volume24h, liquidity, priceUsd }
 */
export const extractMarketData = (pairs) => {
  if (!pairs || pairs.length === 0) {
    return {
      marketCap: null,
      volume24h: null,
      liquidity: null,
      priceUsd: null,
    };
  }

  // Find the pair with highest liquidity (most active)
  const bestPair = pairs.reduce((best, current) => {
    const bestLiq = best?.liquidity?.usd || 0;
    const currentLiq = current?.liquidity?.usd || 0;
    return currentLiq > bestLiq ? current : best;
  }, pairs[0]);

  return {
    marketCap: bestPair?.marketCap || null,
    volume24h: bestPair?.volume?.h24 || bestPair?.volume?.['24h'] || null,
    liquidity: bestPair?.liquidity?.usd || null,
    priceUsd: bestPair?.priceUsd || null,
  };
};

/**
 * Convert chainId to DexScreener format
 * @param {string} chainId - Chain ID in various formats
 * @returns {string} DexScreener chain ID
 */
const convertToDexScreenerChainId = (chainId) => {
  const chainMap = {
    "solana": "solana",
    "0x1": "ethereum",
    "ethereum": "ethereum",
    "eth": "ethereum",
    "0x38": "bsc",
    "bsc": "bsc",
    "binance": "bsc",
    "0x89": "polygon",
    "polygon": "polygon",
    "0xa4b1": "arbitrum",
    "arbitrum": "arbitrum",
    "0x2105": "base",
    "base": "base",
    "0xa": "optimism",
    "optimism": "optimism",
    "0xa86a": "avalanche",
    "avalanche": "avalanche",
    "0xe708": "linea",
    "linea": "linea",
    "0xfa": "fantom",
    "fantom": "fantom",
    "0x171": "pulsechain",
    "pulse": "pulsechain",
    "0x7e4": "ronin",
    "ronin": "ronin",
  };
  
  return chainMap[chainId] || chainId;
};

/**
 * Get comprehensive pair data from DexScreener (300 requests/min)
 * Returns all data needed for TokenInfo component: metadata, stats, volume, price changes, etc.
 * 
 * @param {string} chainId - Chain ID (e.g., "solana", "0x1", "ethereum")
 * @param {string} pairAddress - Pair address
 * @returns {Promise<Object>} Comprehensive pair data object
 */
export const getComprehensivePairData = async (chainId, pairAddress) => {
  try {
    // Convert chainId to DexScreener format
    const dexScreenerChainId = convertToDexScreenerChainId(chainId);
    const url = `${DEXSCREENER_BASE_URL}/latest/dex/pairs/${dexScreenerChainId}/${pairAddress}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`DexScreener API error: ${response.status}`);
    }

    const data = await response.json();
    
    // Extract the first pair from the response
    const pair = data?.pairs?.[0] || null;
    
    if (!pair) {
      return null;
    }

    // Log all available fields in the DexScreener response to see what we're missing
    console.log("📊 [DexScreener] Full pair response structure:", {
      allKeys: Object.keys(pair),
      priceChange: pair.priceChange,
      priceChangeKeys: pair.priceChange ? Object.keys(pair.priceChange) : null,
      priceChangeM5: pair.priceChange?.m5,
      priceChangeH1: pair.priceChange?.h1,
      priceChangeH4: pair.priceChange?.h4,
      priceChangeH24: pair.priceChange?.h24,
      priceUsd: pair.priceUsd,
      txns: pair.txns,
      txnsKeys: pair.txns ? Object.keys(pair.txns) : null,
      volume: pair.volume,
      volumeKeys: pair.volume ? Object.keys(pair.volume) : null,
      hasBuyVolume: !!pair.buyVolume || !!pair.volume?.buy || !!pair.volumeBuy,
      hasSellVolume: !!pair.sellVolume || !!pair.volume?.sell || !!pair.volumeSell,
      hasBuyers: !!pair.buyers || !!pair.txns?.h24?.buyers || !!pair.uniqueBuyers,
      hasSellers: !!pair.sellers || !!pair.txns?.h24?.sellers || !!pair.uniqueSellers,
      rawPair: pair
    });

    // Map DexScreener response to our internal format
    return {
      // Token metadata
      tokenMetadata: {
        name: pair.baseToken?.name || null,
        symbol: pair.baseToken?.symbol || null,
        address: pair.baseToken?.address || null,
        logo: pair.info?.imageUrl || null,
        totalSupplyFormatted: null, // Not available in DexScreener
        total_supply_formatted: null,
        categories: null, // Not available in DexScreener
        links: {
          website: pair.info?.websites?.[0]?.url || null,
          twitter: (() => {
            const twitterSocial = pair.info?.socials?.find(s => 
              s.platform?.toLowerCase() === 'twitter' || s.platform?.toLowerCase() === 'x'
            );
            if (twitterSocial?.url) return twitterSocial.url;
            if (twitterSocial?.handle) {
              // Handle can be with or without @
              const handle = twitterSocial.handle.replace(/^@/, '');
              return `https://x.com/${handle}`;
            }
            return null;
          })(),
          telegram: (() => {
            const telegramSocial = pair.info?.socials?.find(s => 
              s.platform?.toLowerCase() === 'telegram'
            );
            if (telegramSocial?.url) return telegramSocial.url;
            if (telegramSocial?.handle) {
              const handle = telegramSocial.handle.replace(/^@/, '');
              return `https://t.me/${handle}`;
            }
            return null;
          })(),
          discord: (() => {
            const discordSocial = pair.info?.socials?.find(s => 
              s.platform?.toLowerCase() === 'discord'
            );
            if (discordSocial?.url) return discordSocial.url;
            return null;
          })(),
        },
      },
      
      // Pair info (label and exchange name)
      pairInfo: {
        pairLabel: pair.baseToken?.symbol && pair.quoteToken?.symbol 
          ? `${pair.baseToken.symbol}/${pair.quoteToken.symbol}`
          : "Unknown Pair",
        exchangeName: pair.dexId || "Unknown DEX",
      },
      
      // Pair stats (price changes, transactions, volumes)
      pairStats: {
        currentUsdPrice: parseFloat(pair.priceUsd) || 0,
        currentNativePrice: parseFloat(pair.priceNative) || 0,
        totalLiquidityUsd: pair.liquidity?.usd || 0,
        volume24h: pair.volume?.h24 || 0,
        pricePercentChange: (() => {
          // Log the entire priceChange object to see what we're working with
          console.log("💰 [DexScreener] priceChange object:", {
            priceChange: pair.priceChange,
            priceChangeType: typeof pair.priceChange,
            priceChangeKeys: pair.priceChange ? Object.keys(pair.priceChange) : null,
            priceChangeEntries: pair.priceChange ? Object.entries(pair.priceChange).map(([k, v]) => ({ key: k, value: v, type: typeof v })) : null,
            hasPriceChange: !!pair.priceChange,
          });

          if (!pair.priceChange || typeof pair.priceChange !== 'object') {
            console.warn("⚠️ [DexScreener] No priceChange object found in response");
            return {
              '5min': 0,
              '1h': 0,
              '24h': 0,
            };
          }

          // Helper to safely extract and parse price change values
          const getPriceChange = (possibleKeys, defaultValue = 0) => {
            for (const key of possibleKeys) {
              const value = pair.priceChange[key];
              if (value !== undefined && value !== null) {
                // Convert to number if it's a string
                if (typeof value === 'string') {
                  const parsed = parseFloat(value);
                  if (!isNaN(parsed)) return parsed;
                }
                // Return as-is if it's already a number
                if (typeof value === 'number' && !isNaN(value)) {
                  return value;
                }
              }
            }
            return defaultValue;
          };

          // Map all possible key variations for each timeframe
          const result = {
            '5min': getPriceChange(['m5', '5m', 'm5min', '5min', 'm', 'M5', '5M'], 0),
            '1h': getPriceChange(['h1', '1h', 'h1hr', '1hr', 'H1', '1H'], 0),
            '4h': getPriceChange(['h4', '4h', 'h4hr', '4hr', 'H4', '4H'], 0),
            '24h': getPriceChange(['h24', '24h', 'h24hr', '24hr', 'd1', '1d', 'D1', '1D', 'H24', '24H'], 0),
          };

          console.log("💰 [DexScreener] Extracted pricePercentChange:", result);
          
          // If all are 0, log a warning
          if (Object.values(result).every(v => v === 0)) {
            console.warn("⚠️ [DexScreener] All price changes are 0. Available keys in priceChange:", Object.keys(pair.priceChange));
          }
          
          return result;
        })(),
        buys: {
          '5min': pair.txns?.m5?.buys || 0,
          '1h': pair.txns?.h1?.buys || 0,
          '4h': pair.txns?.h4?.buys || 0,
          '24h': pair.txns?.h24?.buys || 0,
        },
        sells: {
          '5min': pair.txns?.m5?.sells || 0,
          '1h': pair.txns?.h1?.sells || 0,
          '4h': pair.txns?.h4?.sells || 0,
          '24h': pair.txns?.h24?.sells || 0,
        },
        buyVolume: {
          '5min': pair.volume?.m5Buy || pair.volumeBuy?.m5 || pair.buyVolume?.m5 || 0,
          '1h': pair.volume?.h1Buy || pair.volumeBuy?.h1 || pair.buyVolume?.h1 || 0,
          '4h': pair.volume?.h4Buy || pair.volumeBuy?.h4 || pair.buyVolume?.h4 || 0,
          '24h': pair.volume?.h24Buy || pair.volumeBuy?.h24 || pair.buyVolume?.h24 || 0,
        },
        sellVolume: {
          '5min': pair.volume?.m5Sell || pair.volumeSell?.m5 || pair.sellVolume?.m5 || 0,
          '1h': pair.volume?.h1Sell || pair.volumeSell?.h1 || pair.sellVolume?.h1 || 0,
          '4h': pair.volume?.h4Sell || pair.volumeSell?.h4 || pair.sellVolume?.h4 || 0,
          '24h': pair.volume?.h24Sell || pair.volumeSell?.h24 || pair.sellVolume?.h24 || 0,
        },
        buyers: {
          '5min': pair.txns?.m5?.buyers || pair.buyers?.m5 || 0,
          '1h': pair.txns?.h1?.buyers || pair.buyers?.h1 || 0,
          '4h': pair.txns?.h4?.buyers || pair.buyers?.h4 || 0,
          '24h': pair.txns?.h24?.buyers || pair.buyers?.h24 || 0,
        },
        sellers: {
          '5min': pair.txns?.m5?.sellers || pair.sellers?.m5 || 0,
          '1h': pair.txns?.h1?.sellers || pair.sellers?.h1 || 0,
          '4h': pair.txns?.h4?.sellers || pair.sellers?.h4 || 0,
          '24h': pair.txns?.h24?.sellers || pair.sellers?.h24 || 0,
        },
        totalVolume: {
          '5min': pair.volume?.m5 || pair.volume?.h5 || 0,
          '1h': pair.volume?.h1 || 0,
          '4h': pair.volume?.h4 || 0,
          '24h': pair.volume?.h24 || 0,
        },
        pairCreated: pair.pairCreatedAt ? new Date(pair.pairCreatedAt * 1000) : null,
      },
      
      // Market data
      marketData: {
        marketCap: pair.marketCap || null,
        fdv: pair.fdv || null,
        volume24h: pair.volume?.h24 || null,
        liquidity: pair.liquidity?.usd || null,
        priceUsd: parseFloat(pair.priceUsd) || null,
      },
      
      // Raw pair data for reference
      rawPair: pair,
    };
  } catch (error) {
    console.error('Error fetching comprehensive pair data from DexScreener:', error);
    throw error;
  }
};

